// src/pages/ESG/ESGFrontPage.jsx
import React, { useEffect } from "react";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import EsgContentPage, { VISIBLE_MENU_ITEMS, slugify } from "./EsgContentPage";
import { getLangFromCookie } from "../../../../Utils/LangCookie";

const HERO_TEXT = {
  ZH: {
    subtitle:
      "台灣水禾專注於食品衛生、病媒防治與環境清潔解決方案。\n我們以永續為企業行動基礎，致力於讓環境與生活更美好。",
    heading: "「提升生活品質 守護環境」",
  },
  EN: {
    subtitle:
      '"Taiwan SEHO provides integrated hygiene solutions, advancing sustainability to make daily life cleaner, safer, and more comfortable."',
    heading: "Life & Environment improvement",
  },
  JP: {
    subtitle:
      "台湾水禾は、病媒防除・食品衛生・環境清掃の総合ソリューションに注力しています。サステナビリティを事業活動の基盤とし、より清潔で安心できる生活環境の実現を目指しています。",
    heading: "「食品安全・衛生・環境守護で、暮らしの質向上を」",
  },
};

const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

function getSectionTitle(item, lang) {
  return item.sectionTitle?.[lang] || item.sectionTitle?.ZH || "";
}

function getSectionSlug(item) {
  return slugify(item.sectionTitle?.ZH || "");
}
const titleLeftPos = {
  ZH: { xs: "24px", md: "auto" },
  EN: { xs: "24px", md: "auto" },
  JP: { xs: "24px", md: "auto" },
};

const titleRightPos = {
  ZH: { xs: "auto", md: "20%", lg: "5%" },
  EN: { xs: "auto", md: "20%", lg: "10%" },
  JP: { xs: "auto", md: "20%", lg: "10%" },
};

const titleTopPos = {
  ZH: { xs: "80%", md: "20%", lg: "57%" },
  EN: { xs: "80%", md: "20%", lg: "57%" },
  JP: { xs: "75%", md: "20%", lg: "57%" },
};

function TopSection({ lang }) {
  const t = HERO_TEXT[lang] || HERO_TEXT.ZH;

  return (
    <Box component="section" sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          position: "relative",
          height: {
            xs: "58svh",
            sm: "62svh",
            md: "68svh",
            lg: "100vh",
          },
          minHeight: { xs: 420, sm: 520, md: 560, lg: 680 },
          maxHeight: { xs: 680, md: 760, lg: "none" },
          backgroundImage: 'url("/img/ESG/top-background.webp")',
          backgroundSize: "cover",
          backgroundPosition: {
            xs: "center",
            md: "center",
            lg: "center",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: titleTopPos[lang] || titleTopPos.ZH,
            left: titleLeftPos[lang] || titleLeftPos.ZH,
            right: titleRightPos[lang] || titleRightPos.ZH,
            transform: "translateY(-50%)",
            textAlign: "left",
            color: "#101010",
            width: {
              xs: "calc(100% - 48px)",
              md: "calc(100% - 144px)",
              lg: "auto",
            },
            maxWidth: { xs: "calc(100% - 48px)", md: 620, lg: 720 },
          }}
        >
          <Typography
            sx={{
              mt: 1,
              fontFamily: fonts[lang],
              fontWeight: 400,
              fontSize: { xs: 20, sm: 26, md: 38, lg: 50 },
              color: "white",
              lineHeight: 1.5,
              display: "inline-block",
              borderRadius: 1,
            }}
          >
            {t.heading}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: fonts[lang],
              fontWeight: 400,
              fontSize: { xs: 15, sm: 17, md: 21, lg: 25 },
              color: "white",
              lineHeight: 1.5,
              display: "inline-block",
              borderRadius: 1,
              whiteSpace: "pre-line",
            }}
          >
            {t.subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const TILE_W = {
  xs: "100%",
  sm: 220,
  md: 230,
  lg: 240,
};

const TILE_H = { xs: 56, md: 64, lg: 66 };

function MenuTile({ label, active, onClick, lang }) {
  const color = getPrimaryColor("b2b");

  return (
    <Paper
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 12 / 8,
        width: TILE_W,
        maxWidth: "100%",
        height: TILE_H,
        px: { xs: 2, md: 2.5 },
        borderRadius: 1.5,
        textDecoration: "none",
        color: active ? "text.primary" : "text.secondary",
        backgroundColor: active ? color : "#fff",
        border: "1px solid",
        borderColor: active ? color : "rgba(0,0,0,0.12)",
        cursor: "pointer",
        boxShadow: active ? "0 8px 20px rgba(0,0,0,.12)" : "none",
        transition:
          "box-shadow .18s ease, transform .18s ease, border-color .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 20px rgba(0,0,0,.08)",
          borderColor: color,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 0,
          height: 0,
          borderTop: "8px solid transparent",
          borderBottom: "8px solid transparent",
          borderLeft: active ? "12px solid #fff" : `12px solid ${color}`,
          flexShrink: 0,
        }}
      />

      <Typography
        sx={{
          fontFamily: fonts[lang],
          fontWeight: 700,
          fontSize: { xs: 15, md: 20 },
          lineHeight: 1.2,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          whiteSpace: "normal",
          color: active ? "#fff" : "text.secondary",
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
    </Paper>
  );
}

function ContentSection({ activeSlug, onSelect, lang }) {
  const t = HERO_TEXT[lang] || HERO_TEXT.ZH;

  return (
    <Box
      id="esg-tab-section"
      component="section"
      sx={{
        py: { xs: 4, md: 6 },

        scrollMarginTop: {
          xs: "88px",
          md: "96px",
          lg: "105px",
        },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: fonts[lang],
            fontWeight: 700,
            fontSize: { xs: 20, md: 24 },
            mb: { xs: 2, md: 3 },
          }}
        >
          {""}
        </Typography>

        <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
          {VISIBLE_MENU_ITEMS.map((item) => {
            const slug = getSectionSlug(item);
            const isActive = slug === activeSlug;
            const label = getSectionTitle(item, lang);

            return (
              <Grid
                key={slug}
                item
                xs={12}
                sm="auto"
                md="auto"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <MenuTile
                  label={label}
                  lang={lang}
                  active={isActive}
                  onClick={() => onSelect(slug)}
                />
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}

export default function ESGFrontPage() {
  const [activeSlug, setActiveSlug] = React.useState(
    getSectionSlug(VISIBLE_MENU_ITEMS[0]),
  );

  const [lang, setLang] = React.useState(() => getLangFromCookie() || "ZH");

  useEffect(() => {
    const syncLang = () => {
      setLang(getLangFromCookie() || "ZH");
    };

    syncLang();
    window.addEventListener("app:languageChanged", syncLang);

    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const handleSelect = (slug) => {
    setActiveSlug(slug);

    window.requestAnimationFrame(() => {
      const el = document.getElementById("esg-content-section");

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopSection lang={lang} />

      <ContentSection
        activeSlug={activeSlug}
        onSelect={handleSelect}
        lang={lang}
      />

      <Box
        id="esg-content-section"
        sx={{
          scrollMarginTop: {
            xs: "120px",
            md: "180px",
            lg: "100px",
          },
        }}
      >
        <EsgContentPage embedded itemSlug={activeSlug} />
      </Box>
    </Box>
  );
}
